/**
 * StockBadge — shared badge rendered on every customer product card.
 *
 * States:
 *   • Sold out            → red pill (track_stock AND (status=OUT_OF_STOCK OR stock ≤ 0))
 *   • Only N left         → orange pill (0 < stock ≤ threshold)
 *   • null (renders nothing) → stock is None (unlimited), track_stock=false, or stock > threshold
 *
 * iter130 — threshold is now tenant-configurable via the admin
 * "Low-stock threshold" setting. Callers should call
 * ``useLowStockThreshold()`` to read the live tenant value and pass
 * it via the ``threshold`` prop (or rely on the DEFAULT fallback for
 * shells that render before tenant context is ready).
 */
import { useTenant } from "../contexts/TenantContext";

export const LOW_STOCK_THRESHOLD = 10;    // safe default / fallback

/** Read the tenant-configured low-stock threshold. Falls back to
 *  the default (10) when the tenant hasn't been loaded yet or
 *  hasn't tuned this setting. */
export function useLowStockThreshold() {
    const { tenant } = useTenant();
    const n = Number(tenant?.low_stock_threshold);
    return Number.isFinite(n) && n > 0 ? n : LOW_STOCK_THRESHOLD;
}

export default function StockBadge({ product, size = "sm", className = "", threshold }) {
    // iter130 — threshold is auto-resolved from tenant context when
    // not explicitly passed. Callsites that already pass a value
    // (tests, storybook, etc.) keep working unchanged.
    const t = useLowStockThreshold();
    const effective = typeof threshold === "number" && threshold > 0 ? threshold : t;
    if (!product) return null;
    const trackStock = product.track_stock !== false;
    if (!trackStock) return null;

    const stock = product.stock;
    const soldOut = product.status === "OUT_OF_STOCK"
        || (stock != null && Number(stock) <= 0);

    const sizeCls = size === "xs"
        ? "text-[9px] px-1.5 py-0.5"
        : "text-[10px] px-1.5 py-0.5";

    if (soldOut) {
        return (
            <span
                data-testid={`stock-badge-soldout-${product.id}`}
                className={`inline-block font-bold uppercase tracking-wider rounded-full bg-red-100 text-red-700 ${sizeCls} ${className}`}
            >
                Sold out
            </span>
        );
    }

    if (stock != null && Number(stock) > 0 && Number(stock) <= effective) {
        return (
            <span
                data-testid={`stock-badge-low-${product.id}`}
                className={`inline-block font-bold uppercase tracking-wider rounded-full bg-orange-100 text-orange-700 ${sizeCls} ${className}`}
            >
                Only {stock} left
            </span>
        );
    }
    return null;
}
